import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { promptInstall } from "@/lib/pwa";

const LINKS = [
  { to: "/music", label: "MUSIC" },
  { to: "/tour", label: "TOUR" },
  { to: "/merch", label: "MERCH" },
  { to: "/label", label: "LABEL" },
  { to: "/gallery", label: "GALLERY" },
  { to: "/contact", label: "CONTACT" },
  { to: "/about", label: "ABOUT" },
  { to: "/subscribe", label: "SUBSCRIBE" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid="main-navbar"
      className="fixed top-0 left-0 z-50 w-full border-b border-white/10 bg-black/60 backdrop-blur-xl"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <Link
          to="/"
          data-testid="nav-logo"
          className="font-anton text-2xl tracking-wide text-white transition-colors duration-300 hover:text-primary"
        >
          MILLZZ<span className="text-primary">.</span>
        </Link>

        <nav className="hidden items-center gap-10 md:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `group relative font-mono text-xs tracking-[0.3em] transition-colors duration-300 ${
                  isActive ? "text-primary" : "text-white/70 hover:text-white"
                }`
              }
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-primary transition-[width] duration-300 group-hover:w-full" />
            </NavLink>
          ))}
          <button
            data-testid="nav-app-button"
            onClick={async () => {
              const ok = await promptInstall();
              if (!ok) toast.info("Open your browser menu and tap 'Add to Home Screen' to install the MILLZZ app.");
            }}
            className="border border-primary/60 px-5 py-2 font-mono text-xs font-bold tracking-[0.2em] text-primary transition-colors duration-300 hover:bg-primary hover:text-black"
          >
            APP
          </button>
          <Link
            to="/merch"
            data-testid="nav-shop-button"
            className="bg-primary px-5 py-2 font-mono text-xs font-bold tracking-[0.2em] text-black transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_#fff]"
          >
            SHOP
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="text-white md:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-white/10 bg-black/90 md:hidden"
          >
            <div className="flex flex-col gap-6 px-6 py-8">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className="font-anton text-3xl text-white transition-colors duration-300 hover:text-primary"
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
